import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import Reveal from "../../public/Reveal";
import StampButton from "../../public/StampButton";
import { PageOpening, Section, SectionTitle, MarginNote, Prose, Rule } from "../../public/Journal";

// Category intros shown in the margin column, keyed by category title.
const CATEGORY_NOTES = {
    "Getting Started": "Start here. Under five minutes from sign-up to first answer.",
    "Feature Documentation": "One entry per compartment and workspace tool.",
    "Partner Integration": "For organizations embedding JPRO on their own sites.",
    "Best Practices": "How experienced users get sharper, faster results.",
    "Troubleshooting": "Common problems and the quickest fix for each.",
};

// One appendix entry: mono type tag, serif title, plain description.
function Entry({ resource, index }) {
    return (
        <Reveal as="li" delay={(index % 2) * 80} className="border-t border-ink/15 py-6 dark:border-paper/15">
            <a href={resource.link} className="group block">
                <div className="flex items-baseline justify-between gap-4">
                    <span className="font-plex-mono text-[11px] uppercase tracking-[0.18em] text-stamp">{resource.type}</span>
                    {resource.external && (
                        <ArrowUpRight aria-label="External link" className="h-3.5 w-3.5 text-ink/40 transition-colors group-hover:text-ink dark:text-paper/40 dark:group-hover:text-paper" />
                    )}
                </div>
                <h3 className="mt-2 font-news text-[24px] leading-[1.15] tracking-[-0.01em]">
                    <span className="link-underline">{resource.title}</span>
                </h3>
                <p className="mt-2 font-plex text-[15px] leading-[1.7] text-ink/70 dark:text-paper/70">{resource.description}</p>
            </a>
        </Reveal>
    );
}

function Resources() {
    const categories = [
        {
            title: "Getting Started",
            resources: [
                {
                    title: "Quick Start Guide",
                    description: "Sign up with email, Google, or Microsoft. Get to your dashboard and start chatting with JPRO in under 5 minutes.",
                    type: "Guide",
                    link: "#quick-start"
                },
                {
                    title: "Account Setup & Profile",
                    description: "Complete your profile, upload an avatar, choose dark or light mode, and set your notification preferences.",
                    type: "Tutorial",
                    link: "#account-setup"
                },
                {
                    title: "Dashboard Overview",
                    description: "Your central hub for Chat, Projects, Studio, JPRO Analyzer, and Documents. See recent activity and jump into any feature.",
                    type: "Guide",
                    link: "#dashboard-overview"
                }
            ]
        },
        {
            title: "Feature Documentation",
            resources: [
                {
                    title: "AIT Lab (Average Income Test)",
                    description: "Run the Average Income Test for LIHTC properties. Upload a rent roll or unit spreadsheet in any layout and JPRO maps the columns, extracts every unit, validates AIT bands, and flags compliance risk. Break results out by building, generate leasing reports, and ask follow-up questions in AIT Chat.",
                    type: "Documentation",
                    link: "#ait-lab-docs"
                },
                // {
                //     title: "Audit Tracker",
                //     description: "Manage the full audit lifecycle across clients. Track each audit's status and due dates, attach and version files, and keep the file request and escalation thread in one place so nothing slips.",
                //     type: "Documentation",
                //     link: "#audit-tracker-docs"
                // },
                {
                    title: "Chat (JPRO Assistant)",
                    description: "Ask questions, upload documents, paste YouTube URLs, or use voice input. Toggle between Fast and Deep reasoning modes depending on how thorough you need JPRO to be.",
                    type: "Documentation",
                    link: "#unified-chat-docs"
                },
                {
                    title: "Projects & Collaboration",
                    description: "Organize chats and documents by property, client, or topic. JPRO remembers context across all chats in a project. Invite team members with Viewer, Member, or Editor roles.",
                    type: "Documentation",
                    link: "#projects-docs"
                },
                {
                    title: "JPRO Analyzer",
                    description: "Upload inspection reports or enter data manually to get a JPRO Index score, risk assessment, market comparison, and exportable report.",
                    type: "Documentation",
                    link: "#jpro-analyzer-docs"
                },
                {
                    title: "Teamspace",
                    description: "Chat with your team in shared channels. Mention a teammate with @name to pull them in, or mention @JPRO to bring the assistant into the conversation for instant answers.",
                    type: "Documentation",
                    link: "#teamspace-docs"
                },
                {
                    title: "My Notes & My Tasks",
                    description: "Capture notes and to-dos as you work. Share notes with teammates, assign tasks, set due dates, and track what's open versus done across your team.",
                    type: "Documentation",
                    link: "#notes-tasks-docs"
                },
                // {
                //     title: "Email",
                //     description: "Connect your Outlook inbox to read, search, and triage email without leaving JPRO. Pull message context straight into a chat or task.",
                //     type: "Documentation",
                //     link: "#email-docs"
                // },
                {
                    title: "Industry Briefs",
                    description: "Stay current on affordable housing news from HUD, NAHMA, NCSHA, and state agencies. Click any item for a JPRO-tailored read on how it affects your work, then save it to Notes or your reading list.",
                    type: "Documentation",
                    link: "#news-docs"
                },
                {
                    title: "Studio",
                    description: "Create on-brand visuals without a designer. Spin up property marketing flyers, leasing and event graphics, social posts, training slides, and newsletter imagery from a simple description. Use Creative Studio to refine it through conversation.",
                    type: "Documentation",
                    link: "#studio-docs"
                },
                {
                    title: "Leaderboards",
                    description: "See team activity and engagement at a glance. Track participation and friendly standings across your organization.",
                    type: "Documentation",
                    link: "#leaderboards-docs"
                },
                {
                    title: "Studio Archive & Chat History",
                    description: "Browse all your uploaded files, generated images, and past conversations. Filter, search, download, or resume any previous chat.",
                    type: "Documentation",
                    link: "#document-management"
                }
            ]
        },
        {
            title: "Partner Integration",
            resources: [
                {
                    title: "Partner Onboarding",
                    description: "Register as a partner to embed JPRO on your website. Access your partner dashboard to manage usage and credentials.",
                    type: "Guide",
                    link: "#partner-onboarding"
                },
                {
                    title: "Embedding JPRO",
                    description: "Add JPRO chat to your website with an embeddable widget. Customize the look and feel to match your brand.",
                    type: "Tutorial",
                    link: "#embedding-guide"
                },
                {
                    title: "API Reference",
                    description: "Full API documentation for partners building custom integrations with JPRO.",
                    type: "API Docs",
                    link: "#api-reference",
                    external: true
                }
            ]
        },
        {
            title: "Best Practices",
            resources: [
                {
                    title: "Getting Better Answers",
                    description: "Be specific in your questions. Upload relevant documents before asking. Use Deep mode for complex compliance questions and Fast mode for quick lookups.",
                    type: "Guide",
                    link: "#prompt-engineering"
                },
                {
                    title: "Preparing AIT Spreadsheets",
                    description: "Give each unit its own row and label columns with clear headers (unit, AMI/designation, rent, manager, programs). JPRO reads almost any layout, but keep LIHTC designations in valid AIT bands (20 through 80) and put any side analytics tables well clear of the main unit data.",
                    type: "Guide",
                    link: "#ait-prep"
                },
                {
                    title: "Running a Clean Audit",
                    description: "Set due dates as soon as an audit opens and request files early. Keep every document and message inside the audit so the full history stays in one place, and let the tracker handle reminders and escalation instead of chasing email threads.",
                    type: "Guide",
                    link: "#audit-tips"
                },
                {
                    title: "Using Projects Effectively",
                    description: "Group related work into projects. Upload shared documents once so JPRO references them in every chat. Invite your team and assign roles based on what they need to do.",
                    type: "Guide",
                    link: "#project-tips"
                },
                {
                    title: "Notes & Tasks That Stick",
                    description: "Capture decisions as notes while they're fresh and turn action items into tasks with an owner and a due date. Share notes with the people who need them so context travels with the work, not just in your head.",
                    type: "Guide",
                    link: "#notes-tips"
                },
                {
                    title: "Collaborating in Teamspace",
                    description: "Mention a teammate with @name when you need them and mention @JPRO to drop the assistant into the thread for a quick answer. Keep channels focused by topic so conversations stay easy to follow.",
                    type: "Guide",
                    link: "#teamspace-tips"
                },
                {
                    title: "Document Preparation",
                    description: "Make sure files have readable text (not scanned images). Clear formatting helps JPRO extract data more accurately, especially for the Analyzer.",
                    type: "Guide",
                    link: "#document-prep"
                },
                {
                    title: "JPRO Analyzer Tips",
                    description: "Include complete property addresses for accurate market data. Review auto-filled estimates before generating your final report.",
                    type: "Guide",
                    link: "#analyzer-optimization"
                },
                {
                    title: "Security & Privacy",
                    description: "Your data is encrypted and private to your account. Documents are only visible to you and project members you invite. You can export or delete your data anytime.",
                    type: "Guide",
                    link: "#security"
                }
            ]
        },
        {
            title: "Troubleshooting",
            resources: [
                {
                    title: "Chat & File Upload Issues",
                    description: "Chat not loading? Refresh the page or clear your browser cache. File won't upload? Check that it's a supported format (PDF, DOCX, XLSX, PNG, JPG) and under 50MB.",
                    type: "FAQ",
                    link: "#chat-issues"
                },
                {
                    title: "Analyzer & Report Problems",
                    description: "Extraction not working? Make sure the document has readable text. Missing market data? Double-check the property address. Report won't download? Check your browser's popup settings.",
                    type: "FAQ",
                    link: "#analyzer-issues"
                },
                {
                    title: "Login & Account Help",
                    description: "Forgot your password? Use the reset link on the login page. Google or Microsoft sign-in not working? Make sure your browser allows popups. Session expired? Just log in again.",
                    type: "FAQ",
                    link: "#auth-issues"
                },
                {
                    title: "Browser & Performance",
                    description: "Use the latest version of Chrome, Firefox, Safari, or Edge for the best experience. For large documents, desktop works better than mobile.",
                    type: "Reference",
                    link: "#browser-compat"
                }
            ]
        }
    ];

    const quickLinks = [
        { label: "System Status", path: "/status" },
        { label: "Accessibility", path: "/accessibility" },
        { label: "Sitemap", path: "/sitemap" },
        { label: "Terms & Conditions", path: "/terms" }
    ];

    return (
        <StandalonePageLayout>
            <PageOpening
                section="§ A"
                label="Appendices"
                title="Everything you need to work with JPRO."
                intro="Guides, documentation, partner material and best practices, filed in one place. Each appendix stands on its own; read them in any order."
            >
                <ol className="flex flex-wrap gap-x-8 gap-y-3 font-plex-mono text-[12px] uppercase tracking-[0.16em]">
                    {categories.map((category, i) => (
                        <li key={category.title}>
                            <a href={`#appendix-${i + 1}`} className="link-underline text-ink/60 hover:text-ink dark:text-paper/60 dark:hover:text-paper">
                                <span className="text-stamp">{String.fromCharCode(65 + i)}.</span> {category.title}
                            </a>
                        </li>
                    ))}
                </ol>
            </PageOpening>

            {categories.map((category, categoryIndex) => (
                <Section
                    key={category.title}
                    id={`appendix-${categoryIndex + 1}`}
                    number={`A.${categoryIndex + 1}`}
                    label={`Appendix ${String.fromCharCode(65 + categoryIndex)}`}
                    aside={
                        <MarginNote>
                            {category.resources.length} {category.resources.length === 1 ? "entry" : "entries"}.{" "}
                            {CATEGORY_NOTES[category.title]}
                        </MarginNote>
                    }
                >
                    <SectionTitle>{category.title}</SectionTitle>
                    <ul className="mt-10 grid gap-x-12 md:grid-cols-2">
                        {category.resources.map((resource, resourceIndex) => (
                            <Entry key={resource.title} resource={resource} index={resourceIndex} />
                        ))}
                    </ul>
                </Section>
            ))}

            {/* Downloads Section */}
                {/* <div className={`mt-12 rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800/50 border border-gray-700' : 'bg-white/50 border border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center gap-3 mb-6">
                        <Download className="w-6 h-6 text-blue-400" />
                        <h2 className="text-2xl font-semibold">Downloadable Resources</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Link
                            to="/docs/user-guide"
                            className={`flex items-center gap-3 p-4 rounded-lg transition-all group text-left ${
                                theme === 'dark'
                                    ? 'bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-gray-500'
                                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <FileText className="w-5 h-5 text-blue-400" />
                            <div className="flex-1">
                                <div className="font-semibold">Complete User Guide</div>
                                <div className="text-xs text-gray-400">All features & workflows • Click to view & download</div>
                            </div>
                            <Download className={`w-4 h-4 transition-colors ${theme === 'dark' ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-gray-900'}`} />
                        </Link>
                        <Link
                            to="/docs/partner-guide"
                            className={`flex items-center gap-3 p-4 rounded-lg transition-all group text-left ${theme === 'dark' ? 'bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-gray-500' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'}`}
                        >
                            <Code className="w-5 h-5 text-blue-400" />
                            <div className="flex-1">
                                <div className="font-semibold">Partner API Documentation</div>
                                <div className="text-xs text-gray-400">Integration & embedding guide • Click to view & download</div>
                            </div>
                            <Download className={`w-4 h-4 transition-colors ${theme === 'dark' ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-gray-900'}`} />
                        </Link>
                        <Link
                            to="/docs/quick-start"
                            className={`flex items-center gap-3 p-4 rounded-lg transition-all group text-left ${theme === 'dark' ? 'bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-gray-500' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'}`}
                        >
                            <Book className="w-5 h-5 text-blue-400" />
                            <div className="flex-1">
                                <div className="font-semibold">Quick Start Cheat Sheet</div>
                                <div className="text-xs text-gray-400">Essential shortcuts & tips • Click to view & download</div>
                            </div>
                            <Download className={`w-4 h-4 transition-colors ${theme === 'dark' ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-gray-900'}`} />
                        </Link>
                    </div>
                </div> */}

            {/* Quick Links */}
            <Section number="A.6" label="See also">
                <SectionTitle>Quick links</SectionTitle>
                <ul className="mt-10 max-w-3xl">
                    {quickLinks.map((link) => (
                        <li key={link.path} className="border-t border-ink/15 dark:border-paper/15">
                            <Link to={link.path} className="group flex items-baseline gap-4 py-4">
                                <span className="font-news text-[22px] leading-tight">
                                    <span className="link-underline">{link.label}</span>
                                </span>
                                <span aria-hidden="true" className="flex-1 translate-y-[-4px] border-b border-dotted border-ink/30 dark:border-paper/30" />
                                <span className="font-plex-mono text-[12px] text-ink/50 dark:text-paper/50">{link.path}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </Section>

            {/* Contact Support */}
            <section className="relative py-16 sm:py-20">
                <Rule strong className="absolute inset-x-0 top-0" />
                <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
                    <div>
                        <SectionTitle>Need more help?</SectionTitle>
                        <Prose className="mt-4">
                            <p>Can&rsquo;t find what you&rsquo;re looking for? Our support team is here to help.</p>
                        </Prose>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                        <a href="mailto:jpro@zeffert.com" className="link-underline font-plex text-[15px] text-ink/80 hover:text-ink dark:text-paper/80 dark:hover:text-paper">
                            jpro@zeffert.com
                        </a>
                        <StampButton to="/dashboard">Go to dashboard</StampButton>
                    </div>
                </Reveal>
            </section>
        </StandalonePageLayout>
    );
}

export default Resources;
